import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Tv, ImageIcon, PlayCircle, Wifi, ArrowRight, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function Stat({ label, value, sub, icon: Icon, tid }) {
  return (
    <div className="signage-card p-5" data-testid={tid}>
      <div className="flex items-start justify-between">
        <p className="label-eyebrow">{label}</p>
        <Icon className="h-4 w-4 text-zinc-400" />
      </div>
      <p className="font-display text-4xl font-black tracking-tight mt-3">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState({ total_media: 0, total_devices: 0, online_devices: 0, total_plays: 0, top_content: [] });

  useEffect(() => {
    api.get("/analytics/summary").then(({ data }) => setS(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <div className="p-6 sm:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Overview</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none mt-2">
            Today on every screen.
          </h1>
        </div>
        <div className="flex gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/viewer.html?guide=id`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-panduan-id"
            className="signage-btn-secondary hidden sm:flex items-center gap-2"
            title="Panduan dalam Bahasa Indonesia"
          >
            <BookOpen className="h-4 w-4" />Panduan (ID)
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/viewer.html?guide=en`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-user-guide"
            className="signage-btn-secondary hidden sm:flex items-center gap-2"
          >
            <BookOpen className="h-4 w-4" />Guide (EN)
          </a>
          <Link to="/admin/devices" className="signage-btn-secondary hidden sm:flex items-center gap-2" data-testid="cta-go-devices">
            Devices <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat label="Media files" value={s.total_media} icon={ImageIcon} tid="stat-media" />
        <Stat label="Devices" value={s.total_devices} sub={`${s.online_devices} online`} icon={Tv} tid="stat-devices" />
        <Stat label="Online now" value={s.online_devices} icon={Wifi} tid="stat-online" />
        <Stat label="Total plays" value={s.total_plays} icon={PlayCircle} tid="stat-plays" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="signage-card p-6 lg:col-span-2">
          <p className="label-eyebrow mb-4">Top content</p>
          {s.top_content.length === 0 ? (
            <p className="text-sm text-zinc-500">No play data yet. Once your devices start displaying scheduled content, top items will appear here.</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {s.top_content.map((c, i) => (
                <li key={c.media_id} className="py-3 flex items-center gap-4">
                  <span className="font-mono-disp text-zinc-400 w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1 text-sm truncate">{c.filename}</span>
                  <span className="font-mono-disp text-sm">{c.plays}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="signage-card p-6">
          <p className="label-eyebrow mb-4">Get started</p>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3"><span className="font-mono-disp text-zinc-400">01</span><Link to="/admin/media" className="hover:underline">Upload media files</Link></li>
            <li className="flex gap-3"><span className="font-mono-disp text-zinc-400">02</span><Link to="/admin/layouts" className="hover:underline">Design a multi-zone layout</Link></li>
            <li className="flex gap-3"><span className="font-mono-disp text-zinc-400">03</span><Link to="/admin/schedules" className="hover:underline">Schedule content per zone</Link></li>
            <li className="flex gap-3"><span className="font-mono-disp text-zinc-400">04</span><Link to="/admin/devices" className="hover:underline">Pair a TV and assign content</Link></li>
          </ol>
        </div>
      </div>
    </div>
  );
}
